/**
 * @component DateRangeFilter
 * @description Wiederverwendbare Datumsbereich-Filterleiste mit Preset-Buttons
 * (Heute, Gestern, Woche, Monat, Quartal, Jahr) und manuellen Datumseingaben.
 * Unterstuetzt benutzerdefinierte Presets und zusaetzliche Controls.
 *
 * @prop {string}     von           - Startdatum YYYY-MM-DD
 * @prop {string}     bis           - Enddatum YYYY-MM-DD
 * @prop {string}     preset        - Aktiver Preset-Key
 * @prop {function}   onChange       - Callback: ({ von, bis, preset }) => void
 * @prop {Array}      [presets]      - Sichtbare Preset-Buttons (Standard: alle)
 * @prop {Array}      [extraPresets] - Zusaetzliche seitenspezifische Buttons
 * @prop {string}     [timestamp]    - Optionaler "Berechnet"-Zeitstempel
 * @prop {ReactNode}  [children]     - Zusaetzliche Controls nach den Datumspickern
 * @prop {string}     [className]    - Optionale CSS-Klassen
 * @usage Eingebunden auf fast allen Datenseiten (Operations, Performance, etc.)
 */
import { useCallback, useMemo } from 'react';
import { getDateRange, getMonthRange, getTodayVienna } from '../utils/dateUtils';
import { useLanguage } from '../context/LanguageContext';

const btnBase = 'px-2.5 py-1 rounded-[8px] text-xs font-medium transition-colors whitespace-nowrap';
const btnActive = 'bg-white text-[var(--text-primary,#1A1A2E)] shadow-sm dark:bg-[var(--bg-card)]';
const btnInactive = 'text-[var(--text-secondary,#4B5563)] hover:text-[var(--text-primary,#1A1A2E)]';
export default function DateRangeFilter({
  von,
  bis,
  preset,
  onChange,
  presets,
  extraPresets,
  timestamp,
  children,
  className = '',
  hideMonthNav = false,
}) {
  const { t, tMonth } = useLanguage();

  const PRESET_GROUPS = useMemo(() => [
    [
      { value: 'heute', label: t('dateFilter.today') },
      { value: 'gestern', label: t('dateFilter.yesterday') },
      { value: 'diese_woche', label: t('dateFilter.thisWeek') },
    ],
    [
      { value: '30T', label: t('dateFilter.last30days') },
      { value: 'monat', label: t('dateFilter.month') },
      { value: 'letzter_monat', label: t('dateFilter.lastMonth') },
    ],
    [
      { value: '3m', label: '3M' },
      { value: '6m', label: '6M' },
      { value: '12m', label: '12M' },
      { value: 'jahr', label: t('dateFilter.year') },
      { value: 'gesamt', label: t('dateFilter.total') },
    ],
  ], [t]);

  const ALL_PRESETS = useMemo(() => PRESET_GROUPS.flat(), [PRESET_GROUPS]);

  // Resolve custom presets into groups, or use default groups
  const resolvedGroups = useMemo(() => {
    if (!presets) return PRESET_GROUPS;
    // Custom presets list → put all into a single group
    const resolved = presets.map(p =>
      typeof p === 'string' ? ALL_PRESETS.find(a => a.value === p) || { value: p, label: p } : p
    );
    return [resolved];
  }, [presets, PRESET_GROUPS, ALL_PRESETS]);

  // Derive nav month from `von`
  const navMonth = useMemo(() => {
    if (!von) {
      const today = getTodayVienna().split('-').map(Number);
      return { year: today[0], month: today[1] };
    }
    const [y, m] = von.split('-').map(Number);
    return { year: y, month: m };
  }, [von]);

  // Can navigate forward? (max = current month)
  const canGoForward = useMemo(() => {
    const [ty, tm] = getTodayVienna().split('-').map(Number);
    return navMonth.year < ty || (navMonth.year === ty && navMonth.month < tm);
  }, [navMonth]);

  const handlePreset = useCallback((p) => {
    const range = getDateRange(p);
    onChange({ von: range.von, bis: range.bis, preset: p });
  }, [onChange]);

  const handleMonthNav = useCallback((dir) => {
    let { year, month } = navMonth;
    if (dir === -1) {
      month--;
      if (month < 1) { month = 12; year--; }
    } else {
      month++;
      if (month > 12) { month = 1; year++; }
      // Clamp to current month
      const [ty, tm] = getTodayVienna().split('-').map(Number);
      if (year > ty || (year === ty && month > tm)) return;
    }
    const range = getMonthRange(year, month);
    onChange({ von: range.von, bis: range.bis, preset: 'nav_monat' });
  }, [navMonth, onChange]);

  const handleCustomDate = useCallback((field, value) => {
    onChange({
      von: field === 'von' ? value : von,
      bis: field === 'bis' ? value : bis,
      preset: 'custom',
    });
  }, [von, bis, onChange]);

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      {/* Quick-Buttons (Pill-Style Tab-Bar) */}
      <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex flex-wrap gap-0.5">
        {resolvedGroups.map((group, gi) => (
          <div key={gi} className="contents">
            {gi > 0 && (
              <div className="h-5 w-px bg-[var(--border-default,#E5E7EB)] mx-1 hidden sm:block" />
            )}
            {group.map(p => (
              <button
                key={p.value}
                onClick={() => handlePreset(p.value)}
                className={`${btnBase} ${preset === p.value ? btnActive : btnInactive}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        ))}

        {/* Extra page-specific presets */}
        {extraPresets?.length > 0 && (
          <>
            <div className="h-5 w-px bg-[var(--border-default,#E5E7EB)] mx-1 hidden sm:block" />
            {extraPresets.map(p => (
              <button
                key={p.value}
                onClick={() => p.onClick ? p.onClick() : handlePreset(p.value)}
                className={`${btnBase} ${preset === p.value ? btnActive : btnInactive}`}
              >
                {p.label}
              </button>
            ))}
          </>
        )}
      </div>

      {/* Monats-Navigation */}
      {!hideMonthNav && (
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleMonthNav(-1)}
            className="w-7 h-7 flex items-center justify-center rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-secondary,#4B5563)] hover:text-[var(--text-primary,#1A1A2E)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
            title={t('dateFilter.prevMonth')}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <span className="text-xs font-medium text-[var(--text-primary,#1A1A2E)] min-w-[100px] text-center select-none">
            {tMonth(navMonth.month - 1)} {navMonth.year}
          </span>
          <button
            onClick={() => handleMonthNav(1)}
            disabled={!canGoForward}
            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${
              canGoForward
                ? 'border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-secondary,#4B5563)] hover:text-[var(--text-primary,#1A1A2E)] hover:bg-[var(--bg-inset,#F8F9FC)]'
                : 'border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-muted,#9CA3AF)] cursor-not-allowed opacity-40'
            }`}
            title={t('dateFilter.nextMonth')}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        </div>
      )}

      {/* Kalender-Picker + children + Timestamp */}
      <div className="flex items-center gap-2 ml-auto">
        <input
          type="date"
          value={von || ''}
          onChange={e => handleCustomDate('von', e.target.value)}
          className="bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] rounded-lg px-2 py-1.5 text-xs text-[var(--text-primary,#1A1A2E)] font-mono focus:outline-none focus:border-[#338edc]/40"
        />
        <span className="text-[var(--text-muted,#9CA3AF)] text-xs">–</span>
        <input
          type="date"
          value={bis || ''}
          onChange={e => handleCustomDate('bis', e.target.value)}
          className="bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] rounded-lg px-2 py-1.5 text-xs text-[var(--text-primary,#1A1A2E)] font-mono focus:outline-none focus:border-[#338edc]/40"
        />

        {children}

        {timestamp && (
          <span className="text-[10px] text-[var(--text-muted,#9CA3AF)] whitespace-nowrap ml-1">
            {t('dateFilter.calculated', { time: timestamp })}
          </span>
        )}
      </div>
    </div>
  );
}
